/**
 * charts-extra — local, dependency-free micro-visualisations for the CRM pages.
 *
 * Union of two families that both live here (the shared @/ui kit stays small):
 *   1. KPI / insight primitives — KPI tiles, sparklines, gauges, bar lists,
 *      donuts, insight badges — used by the Groups & Settings pages.
 *   2. Hand-rolled SVG charts — pie, funnel, weekday×hour heatmap, leaderboard,
 *      trend delta badge — used by the CRM real-time dashboards.
 *
 * No charting lib; every mark is theme-aware (token colors / currentColor) and
 * responsive (viewBox + width 100%), and degrades to a muted placeholder rather
 * than throwing on empty / degenerate data.
 */

import { type ReactNode } from 'react';

import { formatPct } from '../analytics/charts';
import { useT, tr } from '@/i18n';


export type ChartTone = 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'muted';

const TONE_TEXT: Record<ChartTone, string> = {
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  muted: 'text-muted',
};

const TONE_BG_SOFT: Record<ChartTone, string> = {
  primary: 'bg-primary/10 text-primary',
  accent: 'bg-accent/10 text-accent',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/10 text-danger',
  muted: 'bg-surface-2 text-muted',
};

const TONE_FILL: Record<ChartTone, string> = {
  primary: 'bg-primary',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  muted: 'bg-muted',
};

const CARD =
  'rounded-token-lg border border-border bg-surface text-foreground shadow-elevation-1';

function clampPct(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

/** Compact number formatter (1 234 → "1,2k") used across the tiles. */
export function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1).replace('.0', '')}k`;
  return String(Math.round(value));
}

// ---------------------------------------------------------------------------
// Sparkline — inline trend line (+ soft area fill)
// ---------------------------------------------------------------------------

export interface SparklineProps {
  data: number[];
  tone?: ChartTone;
  height?: number;
  /** Draw the soft area under the line. Defaults to true. */
  fill?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function Sparkline({
  data,
  tone = 'primary',
  height = 32,
  fill = true,
  className = '',
  ariaLabel,
}: SparklineProps) {
  const { t } = useT();
  const points = data.filter((n) => Number.isFinite(n));
  if (points.length < 2) {
    return (
      <div
        className={`flex h-8 items-center text-xs text-muted ${className}`.trim()}
        aria-hidden="true"
      >
        <span className="h-px w-full bg-border" />
      </div>
    );
  }
  const w = 100;
  const h = height;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = i * step;
    // 1px inset so the stroke never clips at the top/bottom edge.
    const y = h - 1 - ((v - min) / span) * (h - 2);
    return [x, y] as const;
  });
  const line = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={`${TONE_TEXT[tone]} ${className}`.trim()}
      style={{ width: '100%', height }}
      role="img"
      aria-label={ariaLabel ?? t('routes.crm.chartsExtra.trend')}
    >
      {fill && <polygon points={area} fill="currentColor" opacity={0.12} />}
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Delta pill — signed variation (green up / red down / neutral)
// ---------------------------------------------------------------------------

export interface DeltaPillProps {
  /** Percentage variation; `null` renders a neutral "—". */
  value: number | null | undefined;
  /** When true a positive delta is bad (e.g. churn) and colours invert. */
  invert?: boolean;
  className?: string;
}

export function DeltaPill({ value, invert = false, className = '' }: DeltaPillProps) {
  if (value == null || !Number.isFinite(value)) {
    return (
      <span className={`text-xs font-medium text-muted ${className}`.trim()}>—</span>
    );
  }
  const positive = value > 0;
  const neutral = value === 0;
  const good = invert ? !positive : positive;
  const tone = neutral ? 'muted' : good ? 'success' : 'danger';
  const arrow = neutral ? '→' : positive ? '↑' : '↓';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${TONE_BG_SOFT[tone]} ${className}`.trim()}
    >
      <span aria-hidden="true">{arrow}</span>
      {Math.abs(value).toFixed(Math.abs(value) < 10 ? 1 : 0)}%
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatChip — the KPI tile (label, big value, optional delta / hint / sparkline)
// ---------------------------------------------------------------------------

export interface StatChipProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  delta?: number | null;
  deltaInvert?: boolean;
  tone?: ChartTone;
  icon?: ReactNode;
  spark?: number[];
  /** Render the tile in an "indisponible" muted state (data failed to load). */
  unavailable?: boolean;
  className?: string;
}

export function StatChip({
  label,
  value,
  hint,
  delta,
  deltaInvert,
  tone = 'primary',
  icon,
  spark,
  unavailable = false,
  className = '',
}: StatChipProps) {
  const { t } = useT();
  return (
    <div className={`${CARD} flex flex-col gap-1.5 p-3.5 sm:p-4 ${className}`.trim()}>
      <div className="flex items-center gap-2">
        {icon != null && (
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-token-sm ${TONE_BG_SOFT[tone]}`}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        <span className="min-w-0 truncate text-xs font-medium uppercase tracking-wide text-muted">
          {label}
        </span>
      </div>

      {unavailable ? (
        <span className="text-sm font-medium text-muted">{t('routes.crm.chartsExtra.unavailable')}</span>
      ) : (
        <>
          <div className="flex items-end justify-between gap-2">
            <span className="text-2xl font-bold tabular-nums leading-none text-foreground">
              {value}
            </span>
            {delta !== undefined && <DeltaPill value={delta} invert={deltaInvert} />}
          </div>
          {spark && spark.length > 1 && <Sparkline data={spark} tone={tone} height={28} />}
          {hint != null && <span className="text-xs text-muted">{hint}</span>}
        </>
      )}
    </div>
  );
}

/** Responsive KPI row: 2 tiles on mobile → 4 on desktop. */
export function KpiStrip({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${className}`.trim()}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gauge — radial progress ring with a centred value
// ---------------------------------------------------------------------------

export interface GaugeProps {
  value: number;
  max?: number;
  label?: ReactNode;
  sublabel?: ReactNode;
  tone?: ChartTone;
  size?: number;
  thickness?: number;
  /** Text shown in the centre; defaults to the rounded percentage. */
  centerText?: ReactNode;
  className?: string;
}

export function Gauge({
  value,
  max = 100,
  label,
  sublabel,
  tone = 'primary',
  size = 84,
  thickness = 8,
  centerText,
  className = '',
}: GaugeProps) {
  const { t } = useT();
  const pct = clampPct(value, max);
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  return (
    <div className={`flex flex-col items-center gap-1.5 ${className}`.trim()}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={TONE_TEXT[tone]}
          role="img"
          aria-label={typeof label === 'string' ? `${label}: ${Math.round(pct)}%` : t('routes.crm.chartsExtra.gauge')}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={thickness}
            className="stroke-surface-2"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={thickness}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-bold tabular-nums text-foreground">
            {centerText ?? `${Math.round(pct)}%`}
          </span>
        </div>
      </div>
      {label != null && (
        <span className="text-center text-xs font-medium text-foreground">{label}</span>
      )}
      {sublabel != null && (
        <span className="text-center text-[11px] text-muted">{sublabel}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProgressBar — thin labelled bar (lighter than @/ui CardMeter)
// ---------------------------------------------------------------------------

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: ChartTone;
  label?: ReactNode;
  valueLabel?: ReactNode;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  tone = 'primary',
  label,
  valueLabel,
  className = '',
}: ProgressBarProps) {
  const { t } = useT();
  const pct = clampPct(value, max);
  return (
    <div className={className}>
      {(label != null || valueLabel != null) && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
          {label != null ? (
            <span className="min-w-0 truncate text-muted">{label}</span>
          ) : (
            <span />
          )}
          {valueLabel != null && (
            <span className="shrink-0 font-semibold tabular-nums text-foreground">
              {valueLabel}
            </span>
          )}
        </div>
      )}
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeof label === 'string' ? label : t('routes.crm.chartsExtra.progress')}
      >
        <div
          className={`h-full rounded-full ${TONE_FILL[tone]}`}
          style={{ width: `${pct}%`, transition: 'width 500ms ease-out' }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MiniBars — a compact horizontal bar list (label · bar · value)
// ---------------------------------------------------------------------------

export interface MiniBarItem {
  key: string;
  label: ReactNode;
  value: number;
  tone?: ChartTone;
  /** Optional leading swatch colour (overrides tone for the fill). */
  color?: string;
  valueLabel?: ReactNode;
}

export interface MiniBarsProps {
  items: MiniBarItem[];
  max?: number;
  emptyLabel?: string;
  className?: string;
}

export function MiniBars({
  items,
  max,
  emptyLabel = tr('routes.crm.chartsExtra.noData'),
  className = '',
}: MiniBarsProps) {
  if (items.length === 0) {
    return <p className={`text-sm text-muted ${className}`.trim()}>{emptyLabel}</p>;
  }
  const scale = max ?? Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className={`space-y-2.5 ${className}`.trim()}>
      {items.map((item) => {
        const pct = clampPct(item.value, scale);
        return (
          <li key={item.key} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2.5">
            <span className="flex min-w-0 items-center gap-1.5 truncate text-xs font-medium text-foreground">
              {item.color && (
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
              )}
              {item.label}
            </span>
            <span className="h-2 overflow-hidden rounded-full bg-surface-2">
              <span
                className={`block h-full rounded-full ${item.color ? '' : TONE_FILL[item.tone ?? 'primary']}`}
                style={{
                  width: `${pct}%`,
                  backgroundColor: item.color,
                  transition: 'width 500ms ease-out',
                }}
              />
            </span>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-muted">
              {item.valueLabel ?? item.value}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Donut — distribution ring built from SVG stroke segments + a legend
// ---------------------------------------------------------------------------

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerValue?: ReactNode;
  centerLabel?: ReactNode;
  className?: string;
}

export function Donut({
  segments,
  size = 116,
  thickness = 16,
  centerValue,
  centerLabel,
  className = '',
}: DonutProps) {
  const { t } = useT();
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  let cumulative = 0;
  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`.trim()}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={t('routes.crm.chartsExtra.breakdown')}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={thickness}
            className="stroke-surface-2"
          />
          {total > 0 &&
            segments.map((seg) => {
              const frac = Math.max(0, seg.value) / total;
              const dash = frac * circ;
              const el = (
                <circle
                  key={seg.key}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={thickness}
                  strokeDasharray={`${dash} ${circ - dash}`}
                  strokeDashoffset={-cumulative}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
              );
              cumulative += dash;
              return el;
            })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {centerValue != null && (
            <span className="text-lg font-bold tabular-nums text-foreground">{centerValue}</span>
          )}
          {centerLabel != null && (
            <span className="text-[11px] text-muted">{centerLabel}</span>
          )}
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5">
        {segments.map((seg) => (
          <li key={seg.key} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: seg.color }}
            />
            <span className="min-w-0 flex-1 truncate text-foreground">{seg.label}</span>
            <span className="shrink-0 font-semibold tabular-nums text-muted">{seg.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InsightBadge — a tinted pill for a single insight / callout
// ---------------------------------------------------------------------------

export interface InsightBadgeProps {
  tone?: ChartTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function InsightBadge({
  tone = 'primary',
  icon,
  children,
  className = '',
}: InsightBadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${TONE_BG_SOFT[tone]} ${className}`.trim()}
    >
      {icon != null && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}

/** A row inside an "insights" card: tone dot + text + optional trailing value. */
export function InsightRow({
  tone = 'primary',
  label,
  value,
  className = '',
}: {
  tone?: ChartTone;
  label: ReactNode;
  value?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`.trim()}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_FILL[tone]}`} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
      {value != null && (
        <span className="shrink-0 text-sm font-semibold tabular-nums text-muted">{value}</span>
      )}
    </div>
  );
}

/** Section shell for a widget: bordered card with a title + optional trailing slot. */
export function WidgetCard({
  title,
  trailing,
  children,
  className = '',
}: {
  title: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`${CARD} flex flex-col p-4 ${className}`.trim()}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {trailing}
      </div>
      <div className="flex-1">{children}</div>
    </section>
  );
}

// ===========================================================================
// Hand-rolled SVG charts for the CRM real-time dashboards (pie / funnel /
// heatmap / leaderboard / delta badge).
// ===========================================================================
// Categorical palette (matches the analytics BAR_COLORS hues, as stroke/fill).
const SLICE_COLORS = [
  '#6366f1', // indigo-500
  '#10b981', // emerald-500
  '#0ea5e9', // sky-500
  '#f59e0b', // amber-500
  '#f43f5e', // rose-500
  '#8b5cf6', // violet-500
  '#14b8a6', // teal-500
  '#f97316', // orange-500
];

export interface Slice {
  label: string;
  value: number;
}

// --- Pie / donut ------------------------------------------------------------

export function PieChart({
  data,
  size = 180,
  valueFormat = compactNumber,
}: {
  data: Slice[];
  size?: number;
  valueFormat?: (n: number) => string;
}) {
  const { t } = useT();
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted">{t('routes.crm.chartsExtra.noData2')}</div>
    );
  }
  const r = size / 2;
  const inner = r * 0.58;
  let angle = -Math.PI / 2;
  const arcs = data.map((d, i) => {
    const frac = d.value / total;
    const start = angle;
    const end = angle + frac * Math.PI * 2;
    angle = end;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = r + r * Math.cos(start);
    const y1 = r + r * Math.sin(start);
    const x2 = r + r * Math.cos(end);
    const y2 = r + r * Math.sin(end);
    const path = `M ${r} ${r} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    return { path, color: SLICE_COLORS[i % SLICE_COLORS.length], datum: d, frac };
  });

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={t('routes.crm.chartsExtra.breakdown')}
        className="shrink-0"
      >
        {arcs.map((a, i) => (
          <path key={i} d={a.path} fill={a.color} stroke="var(--color-surface, #fff)" strokeWidth={1} />
        ))}
        {/* Donut hole punched with the surface color */}
        <circle cx={r} cy={r} r={inner} className="fill-surface" />
        <text
          x={r}
          y={r - 4}
          textAnchor="middle"
          className="fill-foreground text-[13px] font-bold"
        >
          {valueFormat(total)}
        </text>
        <text x={r} y={r + 12} textAnchor="middle" className="fill-muted text-[9px]">{t('routes.crm.chartsExtra.total')}</text>
      </svg>
      <ul className="min-w-[8rem] flex-1 space-y-1 text-sm">
        {arcs.map((a, i) => (
          <li key={i} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: a.color }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate capitalize text-foreground" title={a.datum.label}>
              {a.datum.label}
            </span>
            <span className="tabular-nums text-muted">{formatPct(a.frac * 100)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Funnel (stacked descending bars with conversion) -----------------------

export interface FunnelRow {
  label: string;
  count: number;
  value?: number;
  conversion?: number;
  dropoff?: number;
}

export function FunnelChart({
  rows,
  valueFormat = compactNumber,
}: {
  rows: FunnelRow[];
  valueFormat?: (n: number) => string;
}) {
  const { t } = useT();
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted">{t('routes.crm.chartsExtra.noData2')}</div>
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const width = Math.max(6, Math.round((r.count / max) * 100));
        const color = SLICE_COLORS[i % SLICE_COLORS.length];
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-28 shrink-0 truncate text-xs font-medium text-foreground" title={r.label}>
              {r.label}
            </div>
            <div className="relative h-8 flex-1 overflow-hidden rounded-token bg-surface-2">
              <div
                className="flex h-full items-center rounded-token px-2 text-xs font-semibold text-white transition-[width]"
                style={{ width: `${width}%`, backgroundColor: color }}
              >
                <span className="tabular-nums drop-shadow">{valueFormat(r.count)}</span>
              </div>
            </div>
            <div className="w-24 shrink-0 text-right text-xs tabular-nums text-muted">
              {r.conversion != null ? tr('routes.crm.chartsExtra.conversionShort', { pct: formatPct(r.conversion) }) : ''}
              {r.dropoff != null && r.dropoff > 0 ? (
                <span className="ml-1 text-danger">-{formatPct(r.dropoff)}</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Weekday × hour heatmap -------------------------------------------------

const WEEKDAYS = [tr('routes.crm.chartsExtra.mon'), tr('routes.crm.chartsExtra.tue'), tr('routes.crm.chartsExtra.wed'), tr('routes.crm.chartsExtra.thu'), tr('routes.crm.chartsExtra.fri'), tr('routes.crm.chartsExtra.sat'), tr('routes.crm.chartsExtra.sun')];

export interface HeatCell {
  weekday: number; // 0=Mon
  hour: number; // 0..23
  value: number;
}

export function Heatmap({ cells, max }: { cells: HeatCell[]; max: number }) {
  const { t } = useT();
  const grid = new Map<string, number>();
  for (const c of cells) grid.set(`${c.weekday}-${c.hour}`, c.value);
  const peak = Math.max(1, max, ...cells.map((c) => c.value));

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex">
          <div className="w-9 shrink-0" />
          <div className="flex flex-1 gap-px">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="flex-1 text-center text-[8px] text-muted">
                {h % 6 === 0 ? h : ''}
              </div>
            ))}
          </div>
        </div>
        {WEEKDAYS.map((day, wd) => (
          <div key={wd} className="mt-px flex items-center">
            <div className="w-9 shrink-0 pr-1 text-right text-[10px] text-muted">{day}</div>
            <div className="flex flex-1 gap-px">
              {Array.from({ length: 24 }, (_, h) => {
                const v = grid.get(`${wd}-${h}`) ?? 0;
                const intensity = v / peak;
                return (
                  <div
                    key={h}
                    className="aspect-square flex-1 rounded-[2px]"
                    style={{
                      backgroundColor:
                        v === 0
                          ? 'var(--color-surface-2, #eef2f7)'
                          : `rgba(99,102,241,${0.15 + intensity * 0.85})`,
                    }}
                    title={t('routes.crm.chartsExtra.dayHHVInteractionS', { day, h, v })}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Leaderboard ------------------------------------------------------------

export interface LeaderRow {
  label: string;
  value: number;
  rank?: number;
}

export function Leaderboard({
  rows,
  valueFormat = compactNumber,
}: {
  rows: LeaderRow[];
  valueFormat?: (n: number) => string;
}) {
  const { t } = useT();
  if (rows.length === 0) {
    return <div className="py-6 text-center text-sm text-muted">{t('routes.crm.chartsExtra.noTeamData')}</div>;
  }
  const max = Math.max(1, ...rows.map((r) => r.value));
  const medal = ['🥇', '🥈', '🥉'];
  return (
    <ol className="space-y-2">
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className="w-6 shrink-0 text-center text-sm">{medal[i] ?? `${(r.rank ?? i + 1)}.`}</span>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium text-foreground" title={r.label}>
                {r.label}
              </span>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-foreground">
                {valueFormat(r.value)}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(3, Math.round((r.value / max) * 100))}%` }}
              />
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// --- Trend delta badge (period-over-period) ---------------------------------

export function DeltaBadge({ delta }: { delta: number }): ReactNode {
  const { t } = useT();
  if (!Number.isFinite(delta) || delta === 0) {
    return <span className="text-xs text-muted">—</span>;
  }
  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
        up ? 'text-success' : 'text-danger'
      }`}
      title={t('routes.crm.chartsExtra.changeVersusThePreviousPeriod')}
    >
      <span aria-hidden>{up ? '▲' : '▼'}</span>
      {formatPct(Math.abs(delta))}
    </span>
  );
}
